import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Tooltip } from "@/shared/ui/tooltip";
import { ViewModeSwitcher } from "../shared/ViewModeSwitcher";
import { FiltersButton } from "../category-views/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Upload, Edit, ChevronDown, Download, Loader2 } from "lucide-react";
import type { ViewMode } from "../../types";

interface FileExplorerToolbarProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  isFiltersOpen: boolean;
  onFiltersToggle: () => void;
  onUploadOpen: () => void;
  activeFiltersCount: number;
  selectedFilesCount: number;
  onEditAttributes?: () => void;
  onEditTags?: () => void;
  onDownloadAll?: () => void;
  isDownloadingAll?: boolean;
}

export function FileExplorerToolbar({
  viewMode,
  onViewModeChange,
  isFiltersOpen,
  onFiltersToggle,
  onUploadOpen,
  activeFiltersCount,
  selectedFilesCount,
  onEditAttributes,
  onEditTags,
  onDownloadAll,
  isDownloadingAll,
}: FileExplorerToolbarProps) {
  return (
    <div className="flex items-center justify-between border-b border-grid-200 bg-white px-4 py-2 shrink-0">
      <div className="flex items-center gap-2">
        <FiltersButton
          activeFiltersCount={activeFiltersCount}
          isOpen={isFiltersOpen}
          onClick={onFiltersToggle}
        />
      </div>

      {selectedFilesCount > 0 && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={onDownloadAll}
            disabled={isDownloadingAll}
          >
            {isDownloadingAll ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Download All
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="default" size="sm" className="gap-1.5">
                <Edit className="h-4 w-4" />
                {selectedFilesCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="rounded-lg px-1.5 py-0 text-[11px] bg-white/20 text-white"
                  >
                    {selectedFilesCount}
                  </Badge>
                )}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEditAttributes}>
                Attributes
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onEditTags}>Tags</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <div className="flex items-center gap-2">
        <ViewModeSwitcher
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
        />

        <Tooltip content="Upload files" side="bottom">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Upload"
            onClick={onUploadOpen}
          >
            <Upload className="h-4 w-4" />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}
